import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Box, Typography, Grid, CircularProgress,
  Alert, IconButton, Tooltip, Divider, Chip
} from '@mui/material';
import {
  WbSunny, Cloud, Opacity, Thunderstorm, AcUnit, WaterDrop,
  Air, Refresh, Thermostat, Speed, Visibility, CompareArrows
} from '@mui/icons-material';
import { weatherAPI } from 'services/api';

// OpenWeather icon mapping
const WEATHER_ICONS = {
  '01d': { icon: <WbSunny sx={{ fontSize: 48 }} />, color: '#FFB300', bg: '#FFF8E1' },
  '01n': { icon: <WbSunny sx={{ fontSize: 48 }} />, color: '#7986CB', bg: '#E8EAF6' },
  '02d': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#64B5F6', bg: '#E3F2FD' },
  '02n': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#5C6BC0', bg: '#E8EAF6' },
  '03d': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#78909C', bg: '#ECEFF1' },
  '03n': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#546E7A', bg: '#ECEFF1' },
  '04d': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#607D8B', bg: '#ECEFF1' },
  '04n': { icon: <Cloud sx={{ fontSize: 48 }} />, color: '#455A64', bg: '#ECEFF1' },
  '09d': { icon: <Opacity sx={{ fontSize: 48 }} />, color: '#42A5F5', bg: '#E3F2FD' },
  '09n': { icon: <Opacity sx={{ fontSize: 48 }} />, color: '#1E88E5', bg: '#E3F2FD' },
  '10d': { icon: <Opacity sx={{ fontSize: 48 }} />, color: '#29B6F6', bg: '#E1F5FE' },
  '10n': { icon: <Opacity sx={{ fontSize: 48 }} />, color: '#0288D1', bg: '#E1F5FE' },
  '11d': { icon: <Thunderstorm sx={{ fontSize: 48 }} />, color: '#5C6BC0', bg: '#E8EAF6' },
  '11n': { icon: <Thunderstorm sx={{ fontSize: 48 }} />, color: '#3949AB', bg: '#E8EAF6' },
  '13d': { icon: <AcUnit sx={{ fontSize: 48 }} />, color: '#4FC3F7', bg: '#E1F5FE' },
  '13n': { icon: <AcUnit sx={{ fontSize: 48 }} />, color: '#29B6F6', bg: '#E1F5FE' },
  '50d': { icon: <Air sx={{ fontSize: 48 }} />, color: '#90A4AE', bg: '#ECEFF1' },
  '50n': { icon: <Air sx={{ fontSize: 48 }} />, color: '#78909C', bg: '#ECEFF1' },
};

// Small icons for forecast
const FORECAST_ICONS = {
  '01d': <WbSunny sx={{ color: '#FFB300' }} />,
  '01n': <WbSunny sx={{ color: '#7986CB' }} />,
  '02d': <Cloud sx={{ color: '#64B5F6' }} />,
  '02n': <Cloud sx={{ color: '#5C6BC0' }} />,
  '03d': <Cloud sx={{ color: '#78909C' }} />,
  '03n': <Cloud sx={{ color: '#546E7A' }} />,
  '04d': <Cloud sx={{ color: '#607D8B' }} />,
  '04n': <Cloud sx={{ color: '#455A64' }} />,
  '09d': <Opacity sx={{ color: '#42A5F5' }} />,
  '09n': <Opacity sx={{ color: '#1E88E5' }} />,
  '10d': <Opacity sx={{ color: '#29B6F6' }} />,
  '10n': <Opacity sx={{ color: '#0288D1' }} />,
  '11d': <Thunderstorm sx={{ color: '#5C6BC0' }} />,
  '11n': <Thunderstorm sx={{ color: '#3949AB' }} />,
  '13d': <AcUnit sx={{ color: '#4FC3F7' }} />,
  '13n': <AcUnit sx={{ color: '#29B6F6' }} />,
  '50d': <Air sx={{ color: '#90A4AE' }} />,
  '50n': <Air sx={{ color: '#78909C' }} />,
};

const WeatherCard = ({ farmId, pondId, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(null);

  const loadWeather = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (pondId) {
        response = await weatherAPI.getPondWeather(pondId);
      } else if (farmId) {
        response = await weatherAPI.getFarmWeather(farmId);
      } else {
        throw new Error('Farm or Pond ID required');
      }
      
      setWeather(response.data);
    } catch (err) {
      console.error('Weather card error:', err);
      setError(err.response?.data?.detail || 'Failed to load weather');
    } finally {
      setLoading(false);
    }
  }, [farmId, pondId]);

  useEffect(() => {
    loadWeather();
  }, [loadWeather]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' });
  };

  const getWindDirection = (degrees) => {
    if (degrees === null || degrees === undefined) return '';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  if (loading) {
    return (
      <Card sx={{ height: compact ? 'auto' : 280 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: compact ? 'auto' : 280 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Cloud sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Weather</Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            {error.includes('not configured') ? 
              'Weather service not configured. Admin needs to set OpenWeather API key in Settings → Weather Settings.' :
              error
            }
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Weather data is informational only.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!weather) {
    return (
      <Card sx={{ height: compact ? 'auto' : 280 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Cloud sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Weather</Typography>
          </Box>
          <Alert severity="info">
            No weather data available. Weather will load automatically when farm coordinates are set.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const current = weather.current || {};
  const forecast = weather.forecast || [];
  const iconData = WEATHER_ICONS[current.weather_icon] || WEATHER_ICONS['01d'];

  if (compact) {
    // Compact version for dashboard
    return (
      <Card>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: iconData.color }}>
                {React.cloneElement(iconData.icon, { sx: { fontSize: 32 } })}
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {current.temperature ? Math.round(current.temperature) : '--'}°C
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {current.weather_description || current.weather_main || 'Weather'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <WaterDrop sx={{ fontSize: 16, color: 'info.main' }} />
                <Typography variant="caption" display="block">
                  {current.humidity || '--'}%
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Speed sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" display="block">
                  {current.wind_speed ? `${current.wind_speed.toFixed(1)} m/s` : '--'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Full version for pond dashboard
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Weather
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {weather.cached && (
              <Chip 
                label={weather.stale ? "Stale" : "Cached"} 
                size="small" 
                color={weather.stale ? "warning" : "default"}
                variant="outlined"
              />
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadWeather}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Current Weather */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 2, 
          bgcolor: iconData.bg, 
          borderRadius: 2,
          mb: 2
        }}>
          <Box sx={{ color: iconData.color }}>
            {iconData.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {current.temperature ? Math.round(current.temperature) : '--'}°
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {current.weather_description || current.weather_main || 'Weather'}
            </Typography>
            {current.feels_like && (
              <Typography variant="caption" color="text.secondary">
                Feels like {Math.round(current.feels_like)}°
              </Typography>
            )}
          </Box>
          
          {/* Current Stats */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WaterDrop sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography variant="body2">{current.humidity || '--'}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Speed sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                {current.wind_speed ? `${current.wind_speed.toFixed(1)} m/s` : '--'}
                {current.wind_direction !== null && ` ${getWindDirection(current.wind_direction)}`}
              </Typography>
            </Box>
            {current.rain_probability > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Opacity sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ 
                  color: current.rain_probability > 50 ? 'warning.main' : 'inherit',
                  fontWeight: current.rain_probability > 50 ? 600 : 400
                }}>
                  {Math.round(current.rain_probability)}% rain
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Forecast */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          5-Day Forecast
        </Typography>
        
        <Grid container spacing={1}>
          {forecast.slice(0, 5).map((day, index) => (
            <Grid item xs key={index}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 1, 
                borderRadius: 1,
                bgcolor: index === 0 ? 'primary.50' : 'grey.50'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {index === 0 ? 'Today' : formatDate(day.date)}
                </Typography>
                <Box sx={{ my: 0.5 }}>
                  {FORECAST_ICONS[day.weather_icon] || FORECAST_ICONS['01d']}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {day.temp_max ? Math.round(day.temp_max) : '--'}°
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {day.temp_min ? Math.round(day.temp_min) : '--'}°
                </Typography>
                {day.rain_probability > 20 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      icon={<Opacity sx={{ fontSize: '12px !important' }} />}
                      label={`${Math.round(day.rain_probability)}%`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: day.rain_probability > 50 ? 'warning.100' : 'info.100'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Farm Name */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          📍 {weather.farm_name}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default WeatherCard;

